import { useState, useEffect, useMemo } from 'react';
import Api from 'entities/helpers/api';

/**
 * Resolve display labels for proposal ids that may not be present in the
 * default useProposals() browse list (e.g. a linked proposal outside that
 * list's default limit/sort). Batches a single ES query for the ids missing
 * from `knownOptions` instead of falling back to "Proposal #<id>" once the
 * real name is fetchable (NEXDEV-2120).
 *
 * @param {string[]} ids - Currently selected proposal ids (as strings).
 * @param {{value: string, label: string}[]} knownOptions - Already-loaded
 *   options (e.g. from useProposals()) to resolve against before fetching.
 * @returns {Record<string, string>} Map of id -> real label, for whichever
 *   ids have been resolved so far.
 */
const useProposalLabels = (ids, knownOptions) => {
  const knownMap = useMemo(() => {
    const map = {};
    (knownOptions || []).forEach((o) => {
      map[o.value] = o.label;
    });
    return map;
  }, [knownOptions]);

  const missingIds = useMemo(
    () => Array.from(new Set((ids || []).filter((id) => id && !knownMap[String(id)]))).map(String),
    [ids, knownMap]
  );
  const missingKey = missingIds.slice().sort().join(',');

  const [fetchedLabels, setFetchedLabels] = useState({});

  useEffect(() => {
    if (missingIds.length === 0) return undefined;
    let cancelled = false;
    Api.SEARCH.PROPOSAL.post({
      data: JSON.stringify({ must: [{ id: { in: missingIds.map(Number) } }] }),
      params: { limit: missingIds.length },
    })
      .then((resp) => {
        if (cancelled) return;
        const found = {};
        (resp?.items || []).forEach((item) => {
          found[String(item.id)] = item.title || item.name || item.label;
        });
        setFetchedLabels((prev) => ({ ...prev, ...found }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // missingIds is derived from missingKey; re-running on the string avoids
    // firing on every render from a fresh array reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingKey]);

  return useMemo(() => ({ ...fetchedLabels, ...knownMap }), [fetchedLabels, knownMap]);
};

export default useProposalLabels;
